/* eslint-disable complexity */
import type { PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import { PsHttpService } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import {
    GeneralFileUploadApi,
    type SingleUploadFileRequest,
    type BatchUploadFilesRequest,
    type GetDownloadUrlRequest,
    type DeleteFilesRequest,
    type CheckFileExistsRequest,
    type GetFileMetadataRequest,
    type GeneralFileUploadResponse,
    type DeleteFilesResponse,
} from '../general-file-upload-api';
import type { FileMetadataInfo } from '../../types';
import {
    FILE_UPLOAD_API_PATHS,
    DEFAULT_FILE_FOLDER_PATH,
    DEFAULT_MAX_FILE_SIZE_MB,
    DEFAULT_DOWNLOAD_EXPIRE_MINUTES,
} from '../../constants';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';

@injectable()
export class GeneralFileUploadApiImpl extends PsHttpService implements GeneralFileUploadApi {
    get options() {
        return {
            baseURL: coreTelecomApiUrlPrefix,
            headers: {
                'X-API-Version': '2.0',
            },
        };
    }

    async uploadSingleFile(request: SingleUploadFileRequest): Promise<GeneralFileUploadResponse> {
        const formData = new FormData();

        // 添加文件
        formData.append('file', request.file);

        // 添加可选参数
        formData.append('folder_path', request.folder_path ?? DEFAULT_FILE_FOLDER_PATH);
        if (request.file_prefix) {
            formData.append('file_prefix', request.file_prefix);
        }
        formData.append('keep_original_name', String(request.keep_original_name ?? false));
        if (request.tags && request.tags.length > 0) {
            request.tags.forEach(tag => formData.append('tags', tag));
        }

        const result = await this.post<PatSnapSuccessResponseData<GeneralFileUploadResponse>>({
            url: FILE_UPLOAD_API_PATHS.SINGLE_UPLOAD,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return result.data;
    }

    async uploadMultipleFiles(request: BatchUploadFilesRequest): Promise<GeneralFileUploadResponse> {
        const formData = new FormData();

        // 添加文件列表
        for (const file of request.files) {
            formData.append('files', file);
        }

        // 添加可选参数
        formData.append('folder_path', request.folder_path ?? DEFAULT_FILE_FOLDER_PATH);
        if (request.file_prefix) {
            formData.append('file_prefix', request.file_prefix);
        }
        formData.append('keep_original_name', String(request.keep_original_name ?? false));
        if (request.tags && request.tags.length > 0) {
            request.tags.forEach(tag => formData.append('tags', tag));
        }
        if (request.allowed_extensions && request.allowed_extensions.length > 0) {
            request.allowed_extensions.forEach(ext => formData.append('allowed_extensions', ext));
        }
        formData.append(
            'max_file_size_mb',
            String(request.max_file_size_mb ?? DEFAULT_MAX_FILE_SIZE_MB),
        );

        const result = await this.post<PatSnapSuccessResponseData<GeneralFileUploadResponse>>({
            url: FILE_UPLOAD_API_PATHS.BATCH_UPLOAD,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        return result.data;
    }

    async getDownloadUrl(request: GetDownloadUrlRequest): Promise<string> {
        const result = await this.get<PatSnapSuccessResponseData<string>>({
            url: FILE_UPLOAD_API_PATHS.DOWNLOAD_URL,
            params: {
                s3_key: request.s3_key,
                expire_minutes: request.expire_minutes ?? DEFAULT_DOWNLOAD_EXPIRE_MINUTES,
            },
        });

        return result.data;
    }

    async deleteFiles(request: DeleteFilesRequest): Promise<DeleteFilesResponse> {
        const result = await this.delete<PatSnapSuccessResponseData<DeleteFilesResponse>>({
            url: FILE_UPLOAD_API_PATHS.DELETE,
            data: request,
        });

        return result.data;
    }

    async checkFileExists(request: CheckFileExistsRequest): Promise<boolean> {
        const result = await this.get<PatSnapSuccessResponseData<boolean>>({
            url: FILE_UPLOAD_API_PATHS.EXISTS,
            params: {
                s3_key: request.s3_key,
            },
        });

        return result.data;
    }

    async getFileMetadata(request: GetFileMetadataRequest): Promise<FileMetadataInfo> {
        const result = await this.get<PatSnapSuccessResponseData<FileMetadataInfo>>({
            url: FILE_UPLOAD_API_PATHS.METADATA,
            params: {
                s3_key: request.s3_key,
            },
        });

        return result.data;
    }
}
